<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">申报初审</h1>
        <p class="text-sm text-slate-500 mt-1">对科研项目申报书进行全方位合规性审查，包括7大核心审查维度</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">第 2 环节</span>
        <span class="px-3 py-1.5 bg-amber-50 text-amber-600 rounded-full text-xs font-semibold">进行中</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Review Dimensions -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查维度配置</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要审查的维度，系统将自动对申报书进行多维度合规性审查</p>

        <div class="grid grid-cols-3 gap-4 ml-10">
          <label class="relative cursor-pointer" @click="toggleDimension('consistency')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('consistency') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('consistency') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('consistency') ? 'text-blue-700' : 'text-slate-700'">与指南一致性分析</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('consistency') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('consistency')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">研究方向契合度、内容覆盖度、目标一致性</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('plagiarism')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('plagiarism') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('plagiarism') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('plagiarism') ? 'text-blue-700' : 'text-slate-700'">申报书内容查重</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('plagiarism') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('plagiarism')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">与历史项目、已发表论文对比</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('integrity')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('integrity') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('integrity') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('integrity') ? 'text-blue-700' : 'text-slate-700'">科研诚信核查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('integrity') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('integrity')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">失信记录、撤稿记录、AIGC检测</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('format')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('format') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('format') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('format') ? 'text-blue-700' : 'text-slate-700'">申报书格式审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('format') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('format')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">字体字号、段落缩进、图表格式</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('budget')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('budget') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('budget') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('budget') ? 'text-blue-700' : 'text-slate-700'">经费编制审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('budget') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('budget')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">科目比例、测算依据合理性</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('sensitive')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('sensitive') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('sensitive') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('sensitive') ? 'text-blue-700' : 'text-slate-700'">科技敏感问题审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('sensitive') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('sensitive')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">国家机密、伦理争议、敏感技术</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer col-span-3" @click="toggleDimension('limit')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('limit') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('limit') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('limit') ? 'text-blue-700' : 'text-slate-700'">项目限项审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('limit') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('limit')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">核查申请人当前在研项目数量，检查是否超出国拨限项规定</p>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Declaration Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">申报书信息输入</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入申报书编号或上传申报书文件，支持批量审查</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="declarationIds"
              class="premium-input w-full h-32 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请输入申报书编号，多个编号请用换行分隔..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ declarationIds.split('\n').filter(id => id.trim()).length }} 个申报书
            </div>
          </div>

          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              批量上传
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX 格式，最多 50 个文件</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
          启动智能初审分析
        </button>
      </div>
    </div>

    <!-- Statistics Dashboard -->
    <div class="grid grid-cols-4 gap-4">
      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-blue-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">审查时间</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">12<span class="text-lg text-slate-400 ml-1">min</span></div>
          <div class="text-xs text-slate-500 mt-1">平均耗时</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-emerald-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">无问题项</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">156</div>
          <div class="text-xs text-slate-500 mt-1">占比 78%</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-amber-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">建议修改项</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">32</div>
          <div class="text-xs text-slate-500 mt-1">占比 16%</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-red-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">发现问题项</span>
          </div>
          <div class="number-font text-3xl font-bold text-red-400 score-pulse">12</div>
          <div class="text-xs text-slate-500 mt-1">占比 6%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeclareAudit',
  data() {
    return {
      selectedDimensions: ['consistency', 'plagiarism', 'integrity'],
      declarationIds: ''
    }
  },
  methods: {
    toggleDimension(dimension) {
      const index = this.selectedDimensions.indexOf(dimension)
      if (index > -1) {
        this.selectedDimensions.splice(index, 1)
      } else {
        this.selectedDimensions.push(dimension)
      }
    }
  }
}
</script>
